import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useMatch } from 'react-router';
import { Card, Text, Flex, Button } from '../../../../design-system';
import { required, findItemViaPathname } from '../../../../helpers/utils';
import { successRefetch } from '../../../../helpers/data-loader';
import Alert from '../../../../helpers/alert';
import AttachmentsBase from '../../../../components/Attachments';
import ModalForm from '../../../../components/ModalForm';
import FundingSources from './FundingSources';
import Outcomes from './Outcomes';
import Program from './Program';
import { ERR_MSG } from '../../../../globalConstants';

const Attachments = AttachmentsBase as React.ComponentType<any>;

interface ProgramCaseStatementsProps {
  data: any;
  pathname: string;
  refetch: () => void;
  canEdit: boolean;
}

const ProgramCaseStatements = ({
  data,
  pathname,
  refetch,
  canEdit,
}: ProgramCaseStatementsProps) => {
  const { programs, all_files } = data;
  const navigate = useNavigate();
  const [isAddProgramModalOpen, toggleAddProgramModal] = useState(false);
  const [isAttachmentsModalOpen, toggleAttachmentsModal] = useState(false);
  const selectedProgram = findItemViaPathname(programs, pathname);
  const onProgramRoute = useMatch(
    `/vault/${data.id}/program-case-statements/:id`,
  );

  useEffect(() => {
    if (onProgramRoute && !selectedProgram) {
      navigate(-1);
    }
  }, [onProgramRoute, selectedProgram, navigate]);

  const handleProgramCreate = async (values) => {
    try {
      await axios.post(`/client/${data.id}/case`, {
        ...values,
        clientId: data.id,
      });
      toggleAddProgramModal(false);
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  // Returns whether the save succeeded so the Program card can exit edit
  // mode only on success.
  const handleProgramUpdate = async (values) => {
    const { id, client_id, current_budget, annual_revenue } = values;
    try {
      await axios.put(`/client/${client_id}/case/${id}`, {
        ...values,
        current_budget:
          current_budget && String(current_budget).replace(/[^0-9.]/g, ''),
        annual_revenue:
          annual_revenue && String(annual_revenue).replace(/[^0-9.]/g, ''),
      });
      successRefetch(refetch);
      return true;
    } catch (error) {
      Alert.error(ERR_MSG);
      return false;
    }
  };

  const programModal = (
    <ModalForm
      title="Add New Program"
      isOpen={isAddProgramModalOpen}
      onSubmit={handleProgramCreate}
      onClose={() => toggleAddProgramModal(false)}
      fields={[{ name: 'name', label: 'Name', validate: required }]}
    />
  );
  if (!canEdit && programs.length === 0) {
    return (
      <Card>
        <Flex justifyContent="center" p={50}>
          <Text fontWeight={700}>No Programs</Text>
        </Flex>
      </Card>
    );
  }
  return programs.length === 0 ? (
    <Card>
      <Flex justifyContent="center" p={50}>
        <Button
          variant="primaryText"
          type="button"
          onClick={() => toggleAddProgramModal(true)}
          leftIcon="plus"
          leftIconProps={{ size: 20 }}
        >
          Add New Program
        </Button>
        {programModal}
      </Flex>
    </Card>
  ) : (
    <>
      <Card>
        <Card.Header>
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontWeight={700} fontSize={20}>
              Programs
            </Text>
            {canEdit && (
              <Button
                id="add-new-program-button"
                variant="primaryText"
                type="button"
                onClick={() => toggleAddProgramModal(true)}
                leftIcon="plus"
                leftIconProps={{ size: 20 }}
              >
                Add New Program
              </Button>
            )}
          </Flex>
        </Card.Header>
        {programModal}
      </Card>
      {onProgramRoute &&
        (!selectedProgram ? null : (
          <Program
            program={selectedProgram}
            onSubmit={handleProgramUpdate}
            navigate={navigate}
            clientId={data.id}
            refetch={refetch}
            canEdit={canEdit}
          />
        ))}
      {selectedProgram && (
        <>
          <Outcomes
            data={selectedProgram}
            clientId={data.id}
            refetch={refetch}
            canEdit={canEdit}
          />
          <FundingSources
            data={selectedProgram}
            clientId={data.id}
            refetch={refetch}
            canEdit={canEdit}
          />
          <Attachments
            data={
              all_files
                ? all_files.filter(
                    ({ model_type, model_id }) =>
                      model_type === 'VaultPrograms' &&
                      model_id === selectedProgram.id,
                  )
                : []
            }
            canEdit={canEdit}
            isModalOpen={isAttachmentsModalOpen}
            onClick={() => toggleAttachmentsModal(true)}
            onClose={() => toggleAttachmentsModal(false)}
            modelId={selectedProgram.id}
            modelType="VaultPrograms"
            clientId={data.id}
            refetch={refetch}
            maxHeight="15rem"
            disabled={!canEdit}
          />
        </>
      )}
    </>
  );
};

export default ProgramCaseStatements;
